'use client';

import React, { useRef } from 'react';
import { ICommonProps } from '@/types/global';
import { getComponenentSeparation } from '@/utils/styling';
import * as Styled from './ComponentContainer.styled';
import { getPageType } from '@/utils/generic';
import { useAnimation } from '@/hooks/useAnimation';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

export interface IComponentContainer extends ICommonProps {
  as?: 'section' | 'div';
  className?: string;
  children?: React.ReactNode;
  mediaAnimatedRefs?: React.RefObject<HTMLElement>[];
  hasRevealAnimation?: boolean;
}

export const ComponentContainer: React.FC<IComponentContainer> = ({
  width = 'main',
  marginBottom = 'default',
  marginBottomOnMobile = 'default',
  marginTopPage = 'none',
  className = '',
  isProject = false,
  as = 'section',
  children,
  scrollSpeed,
  animationContainer = {
    type: 'none',
    duration: 0.3,
    initialOpacity: 0,
    delayMobile: 0
  },
  scrollAnimation = {
    type: 'none',
  },
  mediaAnimatedRefs,
  hasRevealAnimation = false,
  ...props
}) => {
  const refContainer = useRef<HTMLElement>(null);

  useAnimation({
    animationType: animationContainer.type,
    refContainer: refContainer as React.RefObject<HTMLElement>,
    delay: animationContainer.delay,
    duration:
      scrollAnimation.type === 'fadeOutOnScroll'
        ? 0.4
        : animationContainer.duration,
    initialOpacity: animationContainer.initialOpacity,
    threshold: animationContainer.threshold,
  });

  useScrollAnimation({
    elContainer: mediaAnimatedRefs
      ? mediaAnimatedRefs
          .map((ref) => ref.current)
          .filter((el): el is HTMLElement => el !== null)
      : [refContainer.current as HTMLElement],
    animationType: scrollAnimation.type,
  });

  return (
    <Styled.ComponentContainer
      ref={refContainer}
      width={width}
      className={`${className} component-container ${hasRevealAnimation ? 'reveal-anim' : ''}`}
      marginBtm={getComponenentSeparation(marginBottom)}
      marginBottomOnMobile={getComponenentSeparation(marginBottomOnMobile)}
      marginTopPage={marginTopPage}
      as={as}
      data-animation={animationContainer.type || 'none'}
      pageType={getPageType(isProject)}
      {...(scrollSpeed && { 'data-speed': scrollSpeed })}
      {...props}
    >
      {children}
    </Styled.ComponentContainer>
  );
};
